import { create } from 'zustand';
import { api } from '@/core/lib/apiClient';
import { useSessionStore } from '@/core/stores/sessionStore';
import { useNotificationStore } from '@/core/stores/notificationStore';
import type { Notification } from '@/core/models/notification';

export type FollowMode = 'off' | 'silent' | 'notify';
export interface DiscussionPrefs {
  showHome: boolean;
  autoFollow: boolean;
  rememberPosition: boolean;
  inApp: boolean;
  push: boolean;
  commentSort: 'most-relevant' | 'most-liked' | 'newest' | 'oldest';
}
export interface DiscussionFollow {
  unavailable?: boolean;
  id: number;
  postId: string;
  commentId: string | null;
  mode: FollowMode;
  author: string;
  excerpt: string;
  newCount: number;
  firstNewId: string | null;
  activityAt: string;
}
export interface DiscussionState {
  follows: DiscussionFollow[];
  resumeCommentId: string | null;
  newCount: number;
  firstNewId: string | null;
  snapshot: string;
}
export const DEFAULT_DISCUSSION_PREFS: DiscussionPrefs = {
  showHome: true,
  autoFollow: false,
  rememberPosition: true,
  inApp: false,
  push: false,
  commentSort: 'most-relevant',
};
interface Store {
  owner: string | null;
  prefs: DiscussionPrefs;
  follows: DiscussionFollow[];
  posts: Record<string, DiscussionState>;
  ready: boolean;
  error: string | null;
}
export const useDiscussionStore = create<Store>(() => ({
  owner: null,
  prefs: DEFAULT_DISCUSSION_PREFS,
  follows: [],
  posts: {},
  ready: false,
  error: null,
}));
let generation = 0;
const pending = new Map<string, Promise<void>>();
export const discussionHref = (postId: string, commentId?: string | null) =>
  `/post/${encodeURIComponent(postId)}${commentId ? `?comment=${encodeURIComponent(commentId)}` : ''}`;

function scope(owner: string | null) {
  if (useDiscussionStore.getState().owner === owner) return;
  generation++;
  pending.clear();
  useDiscussionStore.setState({
    owner,
    prefs: DEFAULT_DISCUSSION_PREFS,
    follows: [],
    posts: {},
    ready: false,
    error: null,
  });
}
useSessionStore.subscribe((next, previous) => {
  const active = (s: typeof next) => s.accounts.find((a) => a.useThisAsActive)?.handle;
  if (active(next) !== active(previous)) {
    scope(null);
    // Draft cleanup belongs to explicit auth actions, not transient hydration
    // failures that also clear the in-memory session.
  }
});
async function load(owner: string, force = false) {
  scope(owner);
  if (!force && useDiscussionStore.getState().ready) return;
  const key = 'all';
  if (pending.has(key)) return pending.get(key);
  const gen = generation;
  const work = Promise.all([
    api<DiscussionPrefs>('/discussions/settings/'),
    api<DiscussionFollow[]>('/discussions/?manage=1'),
  ])
    .then(([prefs, follows]) => {
      if (!Array.isArray(follows) || typeof prefs.showHome !== 'boolean')
        throw new Error('Invalid discussion response');
      if (generation === gen)
        useDiscussionStore.setState({ prefs, follows, ready: true, error: null });
    })
    .catch(() => {
      if (generation === gen)
        useDiscussionStore.setState({ ready: true, error: 'Could not load followed discussions.' });
    })
    .finally(() => {
      if (generation === gen) pending.delete(key);
    });
  pending.set(key, work);
  return work;
}
export const discussionsService = {
  scope,
  load,
  async unfollow(owner: string, id: number) {
    const gen = generation;
    await api(`/discussions/subscriptions/${id}/`, { method: 'DELETE' });
    if (gen === generation && useDiscussionStore.getState().owner === owner)
      useDiscussionStore.setState((s) => ({ follows: s.follows.filter((f) => f.id !== id) }));
  },
  async post(owner: string, postId: string) {
    scope(owner);
    const gen = generation;
    const state = await api<DiscussionState>(`/discussions/${encodeURIComponent(postId)}/`);
    if (generation === gen)
      useDiscussionStore.setState((s) => ({ posts: { ...s.posts, [postId]: state } }));
    return state;
  },
  async follow(
    owner: string,
    postId: string,
    commentId: string | null,
    mode: FollowMode,
    enableInApp = false,
  ) {
    const gen = generation;
    const state = await api<DiscussionState>(`/discussions/${encodeURIComponent(postId)}/`, {
      method: 'PUT',
      body: { commentId, mode, enableInApp },
    });
    if (gen === generation && useDiscussionStore.getState().owner === owner) {
      useDiscussionStore.setState((s) => ({
        posts: { ...s.posts, [postId]: state },
        follows: [
          ...s.follows.filter((f) => f.postId !== postId),
          ...state.follows.filter((f) => f.mode !== 'off'),
        ].sort((a, b) => b.activityAt.localeCompare(a.activityAt)),
        prefs: enableInApp && mode === 'notify' ? { ...s.prefs, inApp: true } : s.prefs,
      }));
      // The write has succeeded. A refresh failure must not claim it was rolled back.
      void load(owner, true);
      void api<Notification[]>('/notifications/')
        .then((notifications) => {
          if (gen === generation && Array.isArray(notifications))
            useNotificationStore.getState().setNotifications(notifications);
        })
        .catch(() => {});
    }
  },
  async preferences(owner: string, patch: Partial<DiscussionPrefs>) {
    const gen = generation;
    const prefs = await api<DiscussionPrefs>('/discussions/settings/', {
      method: 'PATCH',
      body: patch,
    });
    if (gen === generation && useDiscussionStore.getState().owner === owner) {
      useDiscussionStore.setState({ prefs });
      if (!prefs.inApp)
        useNotificationStore
          .getState()
          .setNotifications(
            useNotificationStore.getState().notifications.filter((n) => n.kind !== 'discussion'),
          );
    }
  },
  async reading(
    postId: string,
    body: { commentId?: string; snapshot?: string; initialSnapshot?: string },
  ) {
    await api(`/discussions/${encodeURIComponent(postId)}/reading/`, { method: 'POST', body });
  },
};
